import { Skeleton } from "@credit-acceptance/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { getErrorMessage } from "../../lib/query/errors";
import { useViewAllNotes } from "./api/useViewAllNotes";
import type { ViewAllNotesEntry } from "./api/viewAllNotesTypes";
import AuditTable, { type AuditTableColumn } from "./AuditTable";
import {
  ALL_VIEW_ALL_NOTES_FILTER,
  DEFAULT_VIEW_ALL_NOTES_FILTER,
  VIEW_ALL_NOTES_FILTER_LABEL_KEYS,
  VIEW_ALL_NOTES_FILTERS,
  type ViewAllNotesFilterOption,
} from "./viewAllNotesFilters";

interface ViewAllNotesTabProperties {
  approvalId: string;
}

/*
 * Internal-tab View All Notes (OR-22848). Renders the recap-service merged
 * notes list — Entry Date / Notes / User — filtered server-side by the
 * selected caps2-parity category (All, CAPS, DEALER, DSC Analyst, Re-assign,
 * Verification, Advance Adjustment). caps2 step-6 showed this as the Internal
 * tab popup; the Workbench renders it as an Internal / Audit drawer tab.
 *
 * Read-only. Uses the shared AuditTable component with a sticky header so
 * column labels remain visible on long note lists.
 */
function ViewAllNotesTab({ approvalId }: ViewAllNotesTabProperties) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ViewAllNotesFilterOption>(DEFAULT_VIEW_ALL_NOTES_FILTER);
  const { data, isLoading, isError, error } = useViewAllNotes(approvalId, filter);

  const rows = data ?? [];

  const columns: Array<AuditTableColumn<ViewAllNotesEntry>> = [
    {
      key: "entryDate",
      header: t("audit.viewAllNotes.columns.entryDate"),
      render: (row) => <span className="whitespace-nowrap">{row.entryDate}</span>,
    },
    {
      key: "notes",
      header: t("audit.viewAllNotes.columns.notes"),
      render: (row) => row.notes,
    },
    {
      key: "user",
      header: t("audit.viewAllNotes.columns.user"),
      render: (row) => <span className="whitespace-nowrap">{row.user}</span>,
    },
  ];

  return (
    <div className="flex h-full flex-col">
      <div className="mb-3 flex items-center gap-2">
        <label htmlFor="view-all-notes-filter" className="text-primary text-xs font-semibold">
          {t("audit.viewAllNotes.filterLabel")}
        </label>
        <Select value={filter} onValueChange={(value) => setFilter(value as ViewAllNotesFilterOption)}>
          <SelectTrigger id="view-all-notes-filter" size="sm" className="bg-card h-7 w-44 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VIEW_ALL_NOTES_FILTER} className="text-xs">
              {t(`audit.viewAllNotes.filters.${VIEW_ALL_NOTES_FILTER_LABEL_KEYS[ALL_VIEW_ALL_NOTES_FILTER]}`)}
            </SelectItem>
            {VIEW_ALL_NOTES_FILTERS.map((option) => (
              <SelectItem key={option} value={option} className="text-xs">
                {t(`audit.viewAllNotes.filters.${VIEW_ALL_NOTES_FILTER_LABEL_KEYS[option]}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading && (
        <div role="status" aria-label={t("audit.viewAllNotes.loading")} className="px-1 py-3">
          <Skeleton loading active paragraph={{ rows: 6 }} />
        </div>
      )}

      {isError && (
        <p role="alert" className="text-destructive px-1 py-3 text-xs">
          {t("audit.viewAllNotes.loadError", { message: getErrorMessage(error) })}
        </p>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <p className="text-muted-foreground px-1 py-3 text-xs">{t("audit.viewAllNotes.empty")}</p>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <AuditTable caption={t("audit.viewAllNotes.caption")} columns={columns} rows={rows} stickyHeader />
      )}
    </div>
  );
}

export default ViewAllNotesTab;
